import {
  ForwardedRef,
  forwardRef,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  animate,
  m,
  useAnimationControls,
  useMotionValue,
} from "framer-motion";
import { OverlayProps } from "./overlay-props";
import clsx from "clsx";
import { Trans } from "@ui/i18n/trans";
import { ChevronLeftIcon, ArrowBackIcon } from "@ui/icons/material/all-icons";
import { isMobileApp, isRenderedAsIOS } from "@common/core/platform-detection";
import { useSwipeable } from "react-swipeable";
import { usePlayerAmbientStyle } from "@app/web-player/layout/use-player-ambient-style";
import { STACK_SURFACE_Z_INDEX_BASE } from "./overlay-z-index";

const IOS_SWIPE_ZONE = 50;
const IOS_STACKED_UNDERLAY_RATIO = 0.3;
const IOS_STACKED_SURFACE_ATTR = "data-be-stacked-surface";
const IOS_STACKED_UNDERLAY_OFFSET_VAR = "--be-stacked-underlay-offset-x";

const IOS_STACKED_ANIMATIONS = {
  initial: { x: "100%", opacity: 1 },
  enter: { x: 0, opacity: 1 },
  exit: { x: "100%", opacity: 1 },
};

const ANDROID_STACKED_ANIMATIONS = {
  initial: { y: "100%", opacity: 0 },
  enter: { y: 0, opacity: 1 },
  exit: { y: "100%", opacity: 0 },
};

const IOS_STACKED_TRANSITION = {
  type: "tween" as const,
  duration: 0.35,
  ease: [0, 0, 0.58, 1] as [number, number, number, number],
};

const ANDROID_STACKED_TRANSITION = {
  type: "tween" as const,
  duration: 0.225,
  ease: [0, 0, 0.2, 1] as [number, number, number, number],
};

const IOS_CLOSE_SWIPE_TRANSITION = {
  duration: 0.22,
  ease: [0.32, 0.72, 0, 1] as [number, number, number, number],
};

const ANDROID_CLOSE_SWIPE_TRANSITION = {
  duration: 0.195,
  ease: [0.4, 0, 1, 1] as [number, number, number, number],
};

const IOS_REBOUND_SWIPE_TRANSITION = {
  type: "spring" as const,
  stiffness: 380,
  damping: 34,
  mass: 0.88,
};

const ANDROID_REBOUND_SWIPE_TRANSITION = {
  type: "spring" as const,
  stiffness: 440,
  damping: 38,
  mass: 0.88,
};

type StackedUnderlayTarget = {
  kind: "stacked";
  element: HTMLElement;
};

type GenericUnderlayTarget = {
  kind: "generic";
  element: HTMLElement;
  initialStyles: {
    transform: string;
    willChange: string;
    backfaceVisibility: string;
    webkitBackfaceVisibility: string;
  };
};

type UnderlayTarget = StackedUnderlayTarget | GenericUnderlayTarget;

let stackedScrollLockCount = 0;
let stackedScrollY = 0;

function clampNumber(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function getIOSUnderlayOffset() {
  return Math.round(window.innerWidth * IOS_STACKED_UNDERLAY_RATIO);
}

function getIOSUnderlayProgress(deltaX: number) {
  return clampNumber(1 - deltaX / Math.max(window.innerWidth, 1), 0, 1);
}

function setStackedUnderlayOffset(element: HTMLElement, offset: number) {
  element.style.setProperty(IOS_STACKED_UNDERLAY_OFFSET_VAR, `${offset}px`);
}

function clearStackedUnderlayOffset(element: HTMLElement) {
  setStackedUnderlayOffset(element, 0);
}

function createGenericUnderlayTarget(
  element: HTMLElement,
): GenericUnderlayTarget {
  return {
    kind: "generic",
    element,
    initialStyles: {
      transform: element.style.transform,
      willChange: element.style.willChange,
      backfaceVisibility: element.style.backfaceVisibility,
      webkitBackfaceVisibility: element.style.webkitBackfaceVisibility,
    },
  };
}

function resolveUnderlayTargets(currentSurface: HTMLElement): UnderlayTarget[] {
  const stackedSurfaces = Array.from(
    document.querySelectorAll<HTMLElement>(
      `[${IOS_STACKED_SURFACE_ATTR}="true"]`,
    ),
  );
  const currentIndex = stackedSurfaces.findIndex(
    (element) => element === currentSurface,
  );

  if (currentIndex > 0) {
    const previousSurface = stackedSurfaces[currentIndex - 1];
    return previousSurface
      ? [{ kind: "stacked", element: previousSurface }]
      : [];
  }

  const fallbackTargets = [
    ...Array.from(
      document.querySelectorAll<HTMLElement>(".app-ambient-content"),
    ),
    ...Array.from(
      document.querySelectorAll<HTMLElement>(".app-ambient-backdrop"),
    ),
  ].filter((element) => !currentSurface.contains(element));

  if (fallbackTargets.length) {
    return Array.from(new Set(fallbackTargets)).map((element) =>
      createGenericUnderlayTarget(element),
    );
  }

  const mainTarget = Array.from(
    document.querySelectorAll<HTMLElement>("main"),
  ).find((element) => !currentSurface.contains(element));

  return mainTarget ? [createGenericUnderlayTarget(mainTarget)] : [];
}

function applyUnderlayProgress(targets: UnderlayTarget[], progress: number) {
  const offset = -getIOSUnderlayOffset() * clampNumber(progress, 0, 1);

  targets.forEach((target) => {
    if (target.kind === "stacked") {
      const nextOffset = `${offset}px`;
      if (
        target.element.style.getPropertyValue(
          IOS_STACKED_UNDERLAY_OFFSET_VAR,
        ) !== nextOffset
      ) {
        setStackedUnderlayOffset(target.element, offset);
      }
      return;
    }

    const nextTransform =
      Math.abs(offset) > 0.5
        ? `translate3d(${offset}px, 0, 0)`
        : target.initialStyles.transform;

    if (target.element.style.transform !== nextTransform) {
      target.element.style.transform = nextTransform;
    }
  });
}

function prepareUnderlayTargets(targets: UnderlayTarget[]) {
  targets.forEach((target) => {
    if (target.kind === "stacked") {
      return;
    }

    target.element.style.willChange = target.initialStyles.willChange
      ? `${target.initialStyles.willChange}, transform`
      : "transform";
    target.element.style.backfaceVisibility = "hidden";
    target.element.style.webkitBackfaceVisibility = "hidden";
  });
}

function restoreUnderlayTargets(targets: UnderlayTarget[]) {
  targets.forEach((target) => {
    if (target.kind === "stacked") {
      clearStackedUnderlayOffset(target.element);
      return;
    }

    target.element.style.transform = target.initialStyles.transform;
    target.element.style.willChange = target.initialStyles.willChange;
    target.element.style.backfaceVisibility =
      target.initialStyles.backfaceVisibility;
    target.element.style.webkitBackfaceVisibility =
      target.initialStyles.webkitBackfaceVisibility;
  });
}

function lockStackedScroll(isNativeMobileApp: boolean) {
  const bodyEl = document.body;

  if (stackedScrollLockCount === 0) {
    const mainEl = document.querySelector("main");

    if (!isNativeMobileApp) {
      stackedScrollY = window.scrollY;
      bodyEl.style.position = "fixed";
      bodyEl.style.top = `-${stackedScrollY}px`;
      bodyEl.style.left = "0";
      bodyEl.style.right = "0";
    }

    mainEl?.classList.add("overflow-y-hidden");
    bodyEl.classList.add("overflow-y-hidden");
  }

  stackedScrollLockCount += 1;

  return () => {
    stackedScrollLockCount = Math.max(0, stackedScrollLockCount - 1);
    if (stackedScrollLockCount > 0) {
      return;
    }

    const mainEl = document.querySelector("main");

    mainEl?.classList.remove("overflow-y-hidden");
    bodyEl.classList.remove("overflow-y-hidden");

    if (!isNativeMobileApp) {
      bodyEl.style.position = "";
      bodyEl.style.top = "";
      bodyEl.style.left = "";
      bodyEl.style.right = "";
      window.scrollTo(0, stackedScrollY);
    }
  };
}

function setForwardedRef<T>(ref: ForwardedRef<T>, value: T | null) {
  if (!ref) {
    return;
  }

  if (typeof ref === "function") {
    ref(value);
    return;
  }

  (ref as { current: T | null }).current = value;
}

export const Stacked = forwardRef<HTMLDivElement, OverlayProps>(
  (
    {
      children,
      className,
      title = "Go back",
      isOpen = false,
      onClose,
      stackedHeaderRight,
      stackedBodyPadding = true,
      zIndex,
    },
    ref,
  ) => {
    const bodyRef = useRef<HTMLDivElement>(null);
    const surfaceRef = useRef<HTMLDivElement | null>(null);
    const isNativeMobileApp = isMobileApp();
    const renderedAsIOS = isNativeMobileApp && isRenderedAsIOS();
    const shouldDeferBodyMount = renderedAsIOS;
    const { style: ambientStyle } = usePlayerAmbientStyle();
    const animations = renderedAsIOS
      ? IOS_STACKED_ANIMATIONS
      : ANDROID_STACKED_ANIMATIONS;
    const transition = renderedAsIOS
      ? IOS_STACKED_TRANSITION
      : ANDROID_STACKED_TRANSITION;
    const surfaceZIndex = zIndex ?? STACK_SURFACE_Z_INDEX_BASE;

    const x = useMotionValue(0);
    const y = useMotionValue(0);
    const underlayProgress = useMotionValue(0);
    const controls = useAnimationControls();
    const [isSwiping, setIsSwiping] = useState(false);
    const [isBodyReady, setIsBodyReady] = useState(!shouldDeferBodyMount);
    const isSwipingRef = useRef(false);
    const isInteractiveIOSRef = useRef(false);
    const prevDelta = useRef({ x: 0, y: 0 });
    const lastDirRef = useRef<"Right" | "Left" | "Down" | "Up" | null>(null);
    const underlayTargetsRef = useRef<UnderlayTarget[]>([]);
    const underlayAnimationRef = useRef<ReturnType<typeof animate> | null>(
      null,
    );
    const settleAnimationRef = useRef<ReturnType<typeof animate> | null>(null);
    const enterAnimationFrameRef = useRef<number | null>(null);

    const stopUnderlayAnimation = () => {
      underlayAnimationRef.current?.stop();
      underlayAnimationRef.current = null;
    };

    const stopSettleAnimation = () => {
      settleAnimationRef.current?.stop();
      settleAnimationRef.current = null;
    };

    const stopEnterAnimationFrame = () => {
      if (enterAnimationFrameRef.current !== null) {
        window.cancelAnimationFrame(enterAnimationFrameRef.current);
        enterAnimationFrameRef.current = null;
      }
    };

    useLayoutEffect(() => {
      if (!renderedAsIOS || !surfaceRef.current) {
        return;
      }

      underlayTargetsRef.current = resolveUnderlayTargets(surfaceRef.current);
      prepareUnderlayTargets(underlayTargetsRef.current);
      applyUnderlayProgress(underlayTargetsRef.current, 0);

      return () => {
        stopUnderlayAnimation();
        stopSettleAnimation();
        stopEnterAnimationFrame();
        restoreUnderlayTargets(underlayTargetsRef.current);
        underlayTargetsRef.current = [];
      };
    }, [renderedAsIOS]);

    useEffect(() => {
      if (!renderedAsIOS) {
        return;
      }

      return underlayProgress.on("change", (latest) => {
        applyUnderlayProgress(underlayTargetsRef.current, latest);
      });
    }, [renderedAsIOS, underlayProgress]);

    useEffect(() => {
      if (!renderedAsIOS) {
        return;
      }

      const handleResize = () => {
        applyUnderlayProgress(
          underlayTargetsRef.current,
          underlayProgress.get(),
        );
      };

      window.addEventListener("resize", handleResize);

      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }, [renderedAsIOS, underlayProgress]);

    useEffect(() => {
      if (!renderedAsIOS) {
        return;
      }

      return x.on("change", (latest) => {
        if (!isInteractiveIOSRef.current) {
          return;
        }

        underlayProgress.set(getIOSUnderlayProgress(latest));
      });
    }, [renderedAsIOS, underlayProgress, x]);

    const { ref: swipeableRef, ...swipeHandlers } = useSwipeable({
      onSwipeStart: (eventData) => {
        const validDirection = renderedAsIOS
          ? eventData.dir === "Right" && eventData.initial[0] <= IOS_SWIPE_ZONE
          : eventData.dir === "Down";

        const atTopAndClosed =
          renderedAsIOS ||
          (parseInt(y.get().toString(), 10) === 0 &&
            bodyRef.current?.scrollTop === 0);

        if (!validDirection || !atTopAndClosed) {
          return;
        }

        prevDelta.current = { x: 0, y: 0 };
        lastDirRef.current = null;
        stopUnderlayAnimation();
        stopSettleAnimation();
        controls.stop();
        x.set(0);
        y.set(0);

        if (renderedAsIOS) {
          isInteractiveIOSRef.current = true;
          underlayProgress.set(1);
        }

        isSwipingRef.current = true;
        setIsSwiping(true);
      },
      onSwiping: ({ deltaX, deltaY, event }) => {
        if (!isSwipingRef.current) return;

        const dx = deltaX - prevDelta.current.x;
        const dy = deltaY - prevDelta.current.y;
        prevDelta.current = { x: deltaX, y: deltaY };

        if (renderedAsIOS) {
          if (Math.abs(dx) > 0) {
            lastDirRef.current = dx > 0 ? "Right" : "Left";
            if (deltaX > 0) {
              if ("cancelable" in event && event.cancelable) {
                event.preventDefault();
              }
              x.set(deltaX);
              underlayProgress.set(getIOSUnderlayProgress(deltaX));
            }
          }
        } else if (Math.abs(dy) > 0) {
          lastDirRef.current = dy > 0 ? "Down" : "Up";
          if (deltaY > 0) {
            if ("cancelable" in event && event.cancelable) {
              event.preventDefault();
            }
            y.set(deltaY);
          }
        }
      },
      onSwiped: () => {
        if (!isSwipingRef.current) return;
        isSwipingRef.current = false;

        if (renderedAsIOS) {
          if (lastDirRef.current === "Right") {
            const animation = animate(
              x,
              window.innerWidth + 32,
              IOS_CLOSE_SWIPE_TRANSITION,
            );
            settleAnimationRef.current = animation;
            animation.then(() => {
              isInteractiveIOSRef.current = false;
              onClose();
            });
          } else {
            const animation = animate(x, 0, IOS_REBOUND_SWIPE_TRANSITION);
            settleAnimationRef.current = animation;
            animation.then(() => {
              isInteractiveIOSRef.current = false;
              underlayProgress.set(1);
              setIsSwiping(false);
            });
          }
        } else if (lastDirRef.current === "Down") {
          const animation = animate(
            y,
            window.innerHeight + 32,
            ANDROID_CLOSE_SWIPE_TRANSITION,
          );
          settleAnimationRef.current = animation;
          animation.then(() => {
            onClose();
          });
        } else {
          const animation = animate(y, 0, ANDROID_REBOUND_SWIPE_TRANSITION);
          settleAnimationRef.current = animation;
          animation.then(() => {
            setIsSwiping(false);
          });
        }
      },
      trackTouch: true,
      trackMouse: false,
      preventScrollOnSwipe: false,
      touchEventOptions: { passive: false },
    });

    useLayoutEffect(() => {
      const unlockScroll = lockStackedScroll(isNativeMobileApp);
      let cancelled = false;

      if (shouldDeferBodyMount) {
        setIsBodyReady(false);
      } else {
        setIsBodyReady(true);
      }

      controls.set("initial");

      if (renderedAsIOS) {
        underlayProgress.set(0);
        stopUnderlayAnimation();
      }

      enterAnimationFrameRef.current = window.requestAnimationFrame(() => {
        enterAnimationFrameRef.current = null;

        if (cancelled) {
          return;
        }

        if (renderedAsIOS) {
          underlayAnimationRef.current = animate(
            underlayProgress,
            1,
            transition,
          );
        }

        controls.start("enter").then(() => {
          if (!cancelled) {
            setIsBodyReady(true);
          }
        });
      });

      return () => {
        cancelled = true;
        stopEnterAnimationFrame();
        unlockScroll();
      };
    }, [
      controls,
      isNativeMobileApp,
      renderedAsIOS,
      shouldDeferBodyMount,
      transition,
      underlayProgress,
    ]);

    useEffect(() => {
      if (!renderedAsIOS || isOpen || isInteractiveIOSRef.current) {
        return;
      }

      stopUnderlayAnimation();
      underlayAnimationRef.current = animate(underlayProgress, 0, transition);

      return () => {
        stopUnderlayAnimation();
      };
    }, [isOpen, renderedAsIOS, transition, underlayProgress]);

    const surfaceStyle = (
      isNativeMobileApp
        ? {
            ...(ambientStyle ?? {}),
            ...(isSwiping ? (renderedAsIOS ? { x } : { y }) : {}),
            [IOS_STACKED_UNDERLAY_OFFSET_VAR]: "0px",
            willChange: "transform, opacity",
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
            contain: "paint",
            touchAction: renderedAsIOS ? "pan-y" : undefined,
            zIndex: surfaceZIndex,
          }
        : {
            ...(isSwiping ? (renderedAsIOS ? { x } : { y }) : {}),
            [IOS_STACKED_UNDERLAY_OFFSET_VAR]: "0px",
            zIndex: surfaceZIndex,
          }
    ) as any;

    return (
      <m.div
        ref={(node) => {
          surfaceRef.current = node;
          swipeableRef(node);
          setForwardedRef(ref, node);
        }}
        {...{ [IOS_STACKED_SURFACE_ATTR]: "true" }}
        className={clsx(
          "stack fixed top-0 right-0 bottom-0 left-0 flex flex-col",
          className,
          isNativeMobileApp &&
            "app-ambient-stacked player-ambient-scene native-overlay-motion-surface",
        )}
        role="dialog"
        aria-modal="true"
        style={surfaceStyle}
        transformTemplate={(_, generatedTransform) => {
          const transforms = [];

          if (generatedTransform) {
            transforms.push(generatedTransform);
          }

          transforms.push(
            `translateX(var(${IOS_STACKED_UNDERLAY_OFFSET_VAR}, 0px))`,
          );
          transforms.push("translateZ(0)");

          return transforms.join(" ");
        }}
        variants={animations}
        initial="initial"
        animate={controls}
        exit="exit"
        transition={transition}
        {...swipeHandlers}
      >
        <div className="stack-header">
          <button onClick={() => onClose()}>
            {renderedAsIOS ? <ChevronLeftIcon /> : <ArrowBackIcon />}
            <span className="ml-3 mt-3">
              <Trans message={title} />
            </span>
          </button>
          {stackedHeaderRight}
        </div>
        <div
          ref={bodyRef}
          className={clsx(
            "stack-body",
            stackedBodyPadding ? "p-16" : "",
            isSwiping ? "overflow-y-hidden" : "",
            !isBodyReady && "pointer-events-none",
          )}
          aria-busy={!isBodyReady}
        >
          {isBodyReady ? children : null}
        </div>
      </m.div>
    );
  },
);
